import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { DeviceBar } from "../components/DeviceBar";
import { EditorPanel } from "../components/EditorPanel";
import { FilesPanel } from "../components/FilesPanel";
import { InitialLoading } from "../components/InitialLoading";
import { TopBar } from "../components/TopBar";
import { usePad } from "../hooks/usePad";
import { copyTextToClipboard } from "../lib/clipboard";
import { createPadId, getTextStats } from "../lib/pad";
import { MAX_FILE_BYTES } from "../shared";
import { InvalidPad } from "./InvalidPad";

export function PadScreen({ padId, invalidPad = false }: { padId: string; invalidPad?: boolean }) {
  if (invalidPad) {
    return <InvalidPad />;
  }

  return <SyncedPadScreen padId={padId} />;
}

function SyncedPadScreen({ padId }: { padId: string }) {
  const navigate = useNavigate();
  const [uploading, setUploading] = useState(false);
  const [introReady, setIntroReady] = useState(false);
  const { state, text, setText, status, clients, error, initialLoading, reload, uploadFiles, deleteFile, clearPad } = usePad(padId);

  const stats = useMemo(() => getTextStats(text), [text]);

  const showNotice = useCallback((message: string) => {
    toast(message);
  }, []);

  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  useEffect(() => {
    setIntroReady(false);
    const timer = window.setTimeout(() => setIntroReady(true), 900);
    return () => window.clearTimeout(timer);
  }, [padId]);

  const handleFiles = useCallback(
    async (files: FileList | File[]) => {
      const list = Array.from(files);
      if (!list.length) return;

      const oversized = list.find((file) => file.size > MAX_FILE_BYTES);
      if (oversized) {
        showNotice(`${oversized.name} is over 25 MB`);
        return;
      }

      setUploading(true);
      try {
        await uploadFiles(list);
        showNotice(list.length === 1 ? "File added" : `${list.length} files added`);
      } catch (uploadError) {
        showNotice(uploadError instanceof Error ? uploadError.message : "Upload failed");
      } finally {
        setUploading(false);
      }
    },
    [showNotice, uploadFiles],
  );

  const copyText = useCallback(async () => {
    if (!text.length) {
      toast.info("Nothing to copy");
      return;
    }

    const copied = await copyTextToClipboard(text);
    if (copied) {
      toast.success("Text copied");
    } else {
      toast.error("Could not copy text");
    }
  }, [text]);

  const copyLink = useCallback(async () => {
    const copied = await copyTextToClipboard(window.location.href);
    if (copied) {
      toast.success("Link copied");
    } else {
      toast.error("Could not copy link");
    }
  }, []);

  const newPad = useCallback(() => {
    navigate({ to: "/s/$padId", params: { padId: createPadId() } });
  }, [navigate]);

  const doClear = useCallback(async () => {
    await clearPad();
    showNotice("Slip cleared");
  }, [clearPad, showNotice]);

  if (initialLoading || !introReady) {
    return <InitialLoading />;
  }

  return (
    <main className="grid min-h-screen grid-rows-[auto_1fr_auto] overflow-x-hidden bg-white text-neutral-950">
      <TopBar
        status={status}
        clients={clients}
        updatedAt={state.updatedAt}
        onCopyLink={copyLink}
        onNewPad={newPad}
        onClear={doClear}
        onReload={reload}
      />

      <section className="grid min-h-0 min-w-0 grid-cols-1 gap-4 border-y border-neutral-200 bg-neutral-50/60 p-4 lg:grid-cols-[minmax(0,1fr)_minmax(360px,0.42fr)]">
        <EditorPanel text={text} stats={stats} onTextChange={setText} onPasteFiles={(files) => void handleFiles(files)} onCopyText={copyText} />
        <FilesPanel
          files={state.files}
          uploading={uploading}
          onFiles={(files) => void handleFiles(files)}
          onDelete={deleteFile}
          onNotice={showNotice}
        />
      </section>

      <DeviceBar padId={padId} />
    </main>
  );
}
